import { getColorFromRange } from "@/helpers/range/range.helpers";
import { ColorScale } from "@/helpers/range/range.helpers.decl";
import {
  Flex,
  Typography,
  TypographyProps,
  defaultTheme,
} from "@aircall/tractor";
import styled, { getColor } from "@xstyled/styled-components";
import { useMemo } from "react";

type Colors = keyof typeof defaultTheme.colors;
type Rating = "excellent" | "good" | "fair" | "poor" | "very_poor" | "na";
type RatingStyles = {
  backgroundColor: Colors;
  textColor: Colors;
  borderColor: Colors;
};
type AverageProps = {
  // Null is "Not applicable"
  value: number | null;
  variant?: TypographyProps["variant"];
  borderless?: boolean;
};

const styles: Record<Rating, RatingStyles> = {
  excellent: {
    backgroundColor: "surface-success",
    borderColor: "graphic-success",
    textColor: "text-success",
  },
  good: {
    backgroundColor: "surface-interactive-default-active",
    borderColor: "graphic-primary",
    textColor: "text-success",
  },
  fair: {
    backgroundColor: "surface-warning",
    borderColor: "graphic-warning",
    textColor: "text-warning",
  },
  poor: {
    backgroundColor: "surface-critical",
    borderColor: "graphic-critical",
    textColor: "text-critical",
  },
  very_poor: {
    backgroundColor: "surface-interactive-critical-ghost-active",
    borderColor: "graphic-critical-strong",
    textColor: "text-critical",
  },
  na: {
    backgroundColor: "surface-disabled",
    borderColor: "graphic-disabled",
    textColor: "text-placeholder",
  },
};

const StyledFlex = styled(Flex)<{ $rating: Rating; $borderless?: boolean }>`
  width: fit-content;
  padding: xxxs xxs;
  border: ${({ $borderless }) => ($borderless ? "none" : "1px solid")};
  border-radius: 8px;
  border-color: ${({ $rating }) => getColor(styles[$rating].borderColor)};
  background-color: ${({ $rating }) => getColor(styles[$rating].backgroundColor)}
`;

const StyledTypography = styled(Typography)<{ rating: Rating }>`
  text-align: center;
  color: ${({ rating }) => getColor(styles[rating].textColor)}
`;

const ratingScale: ColorScale<Exclude<Rating, "na">> = {
  excellent: [85, 100],
  good: [70, 85],
  fair: [50, 70],
  poor: [20, 50],
  very_poor: [0, 20],
};

export function Average({
  value,
  variant = "bodySemiboldM",
  borderless = false,
}: AverageProps) {
  const isNegative = (value ?? 0) < 0;
  const isGreaterThan100 = (value ?? 0) > 100;

  const rating: Rating = useMemo(() => {
    if (value === null) {
      return "na";
    }

    if (isNegative) {
      return "very_poor";
    }

    if (isGreaterThan100) {
      return "excellent";
    }

    return getColorFromRange(ratingScale)(value) ?? "na";
  }, [isNegative, isGreaterThan100, value]);

  const computedContent = useMemo(() => {
    if (value === null) {
      return "--";
    }

    if (isNegative) {
      return "0 %";
    }

    if (isGreaterThan100) {
      return "100 %";
    }

    return `${value} %`;
  }, [isNegative, isGreaterThan100, value]);

  return (
    <StyledFlex
      data-test={`average-${value}`}
      $rating={rating}
      $borderless={borderless}
      justifyContent="center"
      alignItems="center"
    >
      <StyledTypography rating={rating} variant={variant}>
        {computedContent}
      </StyledTypography>
    </StyledFlex>
  );
}
